import { qualityScore } from '../../utils/evaluation-rubric';
import { runPassed, type ValidateRun } from './validate-runs';
import {
  DEFAULT_MAX_TURNS,
  clampConcurrency,
  clampIterations,
  newCase,
  newSuite,
  parseSuite,
  type SuiteCase,
  type SuiteRun,
  type ValidateSuite,
} from './validate-suites';

/**
 * Moving suites in and out of Validate: its own JSON, the testing tool's
 * (`apps/tester`: a run body `{ suiteConfig, scenarios[] }` or a bare
 * scenarios array), CSV both ways, and a suite run as JUnit XML for CI.
 * Pure, so the page only reads and saves files.
 */

const FORMAT = 'pypestream-validate-suite';

/* ---- JSON ---------------------------------------------------------------- */

export const suiteToJson = (suite: ValidateSuite): string =>
  JSON.stringify(
    {
      format: FORMAT,
      version: 1,
      suite: {
        ...suite,
        // The channel is this solution's: it means nothing elsewhere.
        channelAppId: null,
      },
    },
    null,
    2
  );

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const strings = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((s): s is string => typeof s === 'string' && !!s.trim())
    : [];

/**
 * The tool's guess at whether a step is a goal for the AI customer rather
 * than a message to send (`csv-importer.js` `inferMode`) — used only where a
 * scenario doesn't say its mode.
 */
const GOAL_PATTERNS = [
  /\bturn/i,
  /\bconversation\b/i,
  /\bdiscuss/i,
  /\bexplore/i,
  /\bnavigate/i,
  /\bcomplete\b.*\bflow/i,
  /\bgo\s+through/i,
  /\btest\s+the\b/i,
  /\bverify\b/i,
  /\bcheck\s+if/i,
  /\bwalk\s+through/i,
  /\bfind\s+out/i,
  /\btry\s+to/i,
  /\bsee\s+if/i,
  /\bensure/i,
  /\bmake\s+sure/i,
  /\bask\s+(about|the|if|whether|for)\b/i,
];
const looksLikeGoal = (step: string) => GOAL_PATTERNS.some((p) => p.test(step));

/** One of the tool's scenarios, as a case. */
const caseFromToolScenario = (
  s: Record<string, unknown>,
  makeId: () => string
): SuiteCase | null => {
  const raw = s.instruction ?? s.naturalLanguageInput;
  const steps =
    typeof raw === 'string' ? [raw.trim()].filter(Boolean) : strings(raw);
  if (!steps.length) return null;
  const { mode } = s;
  const criteria = isRecord(s.evaluationCriteria) ? s.evaluationCriteria : {};
  const name = typeof s.name === 'string' ? s.name : '';
  const base = {
    name,
    ...(typeof s.expectedOutcome === 'string' && s.expectedOutcome.trim()
      ? { expectation: s.expectedOutcome }
      : {}),
    ...(strings(criteria.successPatterns).length
      ? { mustSay: strings(criteria.successPatterns) }
      : {}),
    ...(strings(criteria.failurePatterns).length
      ? { mustNotSay: strings(criteria.failurePatterns) }
      : {}),
  };
  const intelligent =
    mode === 'intelligent' ||
    (mode === undefined && steps.every(looksLikeGoal) && steps.length === 1);
  if (intelligent) {
    return newCase(makeId(), { ...base, description: steps.join('\n') });
  }
  // Direct: every step is sent as written. Mixed: goal-like steps go to the
  // AI customer.
  const script = steps
    .map((step) =>
      mode !== 'direct' && looksLikeGoal(step) ? `ai:${step}` : step
    )
    .join('\n');
  return newCase(makeId(), {
    ...base,
    description:
      typeof s.description === 'string' && s.description.trim()
        ? s.description
        : name,
    script,
    maxTurns: DEFAULT_MAX_TURNS,
  });
};

export interface ImportResult {
  suite: ValidateSuite;
  /** What came in, for the confirmation line. */
  note: string;
}

/**
 * A suite from a JSON file: Validate's own export, a bare suite, or the
 * testing tool's run body / scenarios array. Ids are always fresh, so an
 * import never overwrites a suite that's already here.
 */
export const suiteFromJson = (
  text: string,
  makeId: () => string,
  {
    now,
    fallbackName = 'Imported suite',
  }: { now: number; fallbackName?: string }
): ImportResult => {
  let data: unknown;
  try {
    data = JSON.parse(text);
  } catch {
    throw new Error('That file isn’t valid JSON.');
  }

  const own = isRecord(data) && data.format === FORMAT ? data.suite : data;
  if (isRecord(own) && Array.isArray(own.cases)) {
    const parsed = parseSuite({ ...own, id: makeId() });
    if (!parsed) throw new Error('That suite file is missing its cases.');
    return {
      suite: {
        ...parsed,
        cases: parsed.cases.map((c) => ({ ...c, id: makeId() })),
        channelAppId: null,
        createdAt: now,
        updatedAt: now,
      },
      note: `${parsed.cases.length} cases`,
    };
  }

  const scenarios = Array.isArray(data)
    ? data
    : isRecord(data) && Array.isArray(data.scenarios)
      ? data.scenarios
      : null;
  if (!scenarios) {
    throw new Error(
      'Expected a Validate suite, or the testing tool’s scenarios (an array, or { suiteConfig, scenarios }).'
    );
  }
  const config =
    isRecord(data) && isRecord(data.suiteConfig) ? data.suiteConfig : {};
  const cases = scenarios
    .filter(isRecord)
    .map((s) => caseFromToolScenario(s, makeId))
    .filter((c): c is SuiteCase => c !== null);
  if (!cases.length)
    throw new Error('None of those scenarios has steps to run.');
  const name =
    isRecord(data) && typeof data.name === 'string' && data.name.trim()
      ? data.name
      : fallbackName;
  return {
    suite: newSuite(makeId(), now, {
      name,
      cases,
      iterations: clampIterations(config.iterations ?? 1),
      concurrency: clampConcurrency(config.parallel),
    }),
    note: `${cases.length} of ${scenarios.length} scenarios from the testing tool`,
  };
};

/* ---- CSV ----------------------------------------------------------------- */

/** One CSV row, honouring quotes and doubled quotes. */
export const parseCsvRows = (text: string): string[][] => {
  const rows: string[][] = [];
  let row: string[] = [];
  let cell = '';
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        cell += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cell += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ',') {
      row.push(cell);
      cell = '';
    } else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i += 1;
      row.push(cell);
      rows.push(row);
      row = [];
      cell = '';
    } else {
      cell += ch;
    }
  }
  if (cell || row.length) {
    row.push(cell);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => c.trim()));
};

const CSV_COLUMNS = [
  'name',
  'what_happens',
  'expectation',
  'script',
  'must_say',
  'must_not_say',
  'start_at',
  'max_turns',
] as const;

const csvCell = (value: string) =>
  /[",\n\r]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** A suite's cases, one row each; lists are one item per line in the cell. */
export const casesToCsv = (cases: SuiteCase[]): string =>
  [
    CSV_COLUMNS.join(','),
    ...cases.map((c) =>
      [
        c.name,
        c.description,
        c.expectation ?? '',
        c.script ?? '',
        (c.mustSay ?? []).join('\n'),
        (c.mustNotSay ?? []).join('\n'),
        c.startAt,
        String(c.maxTurns),
      ]
        .map(csvCell)
        .join(',')
    ),
  ].join('\n');

/**
 * Cases from a CSV: Validate's own columns, or the testing tool's format —
 * a header row, then one conversation per row, one customer message per cell.
 */
export const casesFromCsv = (
  text: string,
  makeId: () => string
): { cases: SuiteCase[]; note: string } => {
  const rows = parseCsvRows(text);
  if (rows.length < 2)
    throw new Error('Expected a header row and at least one case.');
  const header = rows[0].map((h) => h.trim().toLowerCase());
  const col = (name: string) => header.indexOf(name);
  const lines = (v: string | undefined) =>
    (v ?? '')
      .split(/\r?\n/)
      .map((l) => l.trim())
      .filter(Boolean);

  if (col('what_happens') >= 0 || col('script') >= 0) {
    const get = (r: string[], name: string) =>
      col(name) >= 0 ? (r[col(name)] ?? '') : '';
    const cases = rows.slice(1).map((r) => {
      const startAt = get(r, 'start_at').trim();
      const maxTurns = Number(get(r, 'max_turns'));
      return newCase(makeId(), {
        name: get(r, 'name').trim(),
        description: get(r, 'what_happens').trim(),
        ...(get(r, 'expectation').trim()
          ? { expectation: get(r, 'expectation').trim() }
          : {}),
        ...(get(r, 'script').trim() ? { script: get(r, 'script').trim() } : {}),
        ...(lines(get(r, 'must_say')).length
          ? { mustSay: lines(get(r, 'must_say')) }
          : {}),
        ...(lines(get(r, 'must_not_say')).length
          ? { mustNotSay: lines(get(r, 'must_not_say')) }
          : {}),
        ...(startAt === 'mid' || startAt === 'end' ? { startAt } : {}),
        ...(Number.isFinite(maxTurns) && maxTurns > 0
          ? { maxTurns: Math.min(20, Math.round(maxTurns)) }
          : {}),
      });
    });
    return { cases, note: `${cases.length} cases` };
  }

  // The testing tool's CSV: every cell after the header is a message to send.
  const cases = rows.slice(1).map((r, i) => {
    const steps = r.map((c) => c.trim()).filter(Boolean);
    return newCase(makeId(), {
      name: `Test ${i + 1}`,
      description: `${steps.length} message${steps.length === 1 ? '' : 's'}`,
      script: steps.join('\n'),
    });
  });
  return {
    cases,
    note: `${cases.length} conversations from the testing tool’s CSV`,
  };
};

/* ---- JUnit --------------------------------------------------------------- */

const xml = (s: string) =>
  s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

/** A suite run as JUnit XML: one testcase per run, failures with the reason. */
export const suiteRunToJunit = (
  suiteRun: SuiteRun,
  runs: ValidateRun[]
): string => {
  const failures = runs.filter((r) => runPassed(r) === false).length;
  const errors = runs.filter(
    (r) => runPassed(r) !== false && r.score.status !== 'scored'
  ).length;
  const seconds = (r: ValidateRun) =>
    ((r.endedAt - r.startedAt) / 1000).toFixed(1);
  const cases = runs.map((r) => {
    const name = [
      r.suite?.caseName || r.title,
      r.model ? `[${r.model}]` : '',
      r.suite && r.suite.iteration > 1 ? `#${r.suite.iteration}` : '',
    ]
      .filter(Boolean)
      .join(' ');
    const open = `    <testcase classname="${xml(suiteRun.suiteName)}" name="${xml(name)}" time="${seconds(r)}">`;
    const failedChecks = (r.checks ?? []).filter((c) => !c.passed);
    let body = '';
    if (runPassed(r) === false) {
      const why =
        failedChecks.map((c) => `Failed check: ${c.label}`).join('; ') ||
        (r.score.status === 'scored'
          ? r.score.verdict.autoFail
            ? `Auto-fail: ${r.score.verdict.autoFail}`
            : `Quality ${qualityScore(r.score.verdict)}/100, under the pass mark`
          : '');
      body = `\n      <failure message="${xml(why || 'Failed')}"/>`;
    } else if (r.score.status === 'error') {
      body = `\n      <error message="${xml(r.score.message)}"/>`;
    }
    const out = `\n      <system-out>${xml(
      r.transcript
        .map((t) => `${t.role === 'user' ? 'Customer' : 'Bot'}: ${t.text}`)
        .join('\n')
    )}\n      chat ${xml(r.chatId)}</system-out>`;
    return `${open}${body}${out}\n    </testcase>`;
  });
  const time = (
    ((suiteRun.endedAt ?? Date.now()) - suiteRun.startedAt) /
    1000
  ).toFixed(1);
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    `<testsuites name="${xml(suiteRun.suiteName)}" tests="${runs.length}" failures="${failures}" errors="${errors}" time="${time}">`,
    `  <testsuite name="${xml(suiteRun.suiteName)}" tests="${runs.length}" failures="${failures}" errors="${errors}" time="${time}" timestamp="${new Date(suiteRun.startedAt).toISOString()}">`,
    ...cases,
    '  </testsuite>',
    '</testsuites>',
    '',
  ].join('\n');
};

/* ---- Model comparison ---------------------------------------------------- */

export interface ModelRollup {
  model: string;
  runs: number;
  passed: number;
  scored: number;
  passRate: number | null;
  /** Mean ms to first reply frame. */
  responseMs: number | null;
}

/** Each compared model's pass rate and response time, in the suite's order. */
export const rollupByModel = (
  runs: ValidateRun[],
  models: string[]
): ModelRollup[] =>
  models.map((model) => {
    const own = runs.filter((r) => r.model === model);
    const judged = own.filter((r) => runPassed(r) !== null);
    const passed = judged.filter((r) => runPassed(r) === true).length;
    const replies = own.flatMap((r) =>
      r.transcript.filter(
        (t): t is typeof t & { latencyMs: number } =>
          t.role === 'bot' && !t.seeded && typeof t.latencyMs === 'number'
      )
    );
    return {
      model,
      runs: own.length,
      passed,
      scored: judged.length,
      passRate: judged.length
        ? Math.round((passed / judged.length) * 100)
        : null,
      responseMs: replies.length
        ? Math.round(
            replies.reduce((s, t) => s + t.latencyMs, 0) / replies.length
          )
        : null,
    };
  });
